import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useIdleTimer } from 'react-idle-timer'
import { useAppContext, useSetIsOverviewFullScreen } from '../../../redux/slices/appContext'

export const GenericWheelContext = createContext({})

export const useGetViewConfigByPath = (path) => {
  const { items } = useContext(GenericWheelContext)

  return items.find(x => x.path === path)
}

const IDLE_TIMEOUT_MS = 10000
const useSettingsValues = (settings, dashboardItems, root) => {
  /* Highlighted View - when one view is highlighted, others are lowlighted, also minimap displays which is highlighted */
  const [highlightedView, setHighlightedView] = useState(null)
  const changeHighlightedView = useCallback((view, isHighlighted) => {
    setHighlightedView(isHighlighted ? view : null)
  }, [setHighlightedView])

  /* Idle timeouts - portions of the ui fade away when use is not doing anything for a small period of time */
  const [idle, setIdle] = useState(false)
  const onIdle = useCallback(() => setIdle(true), [setIdle])
  const onActive = useCallback(() => setIdle(false), [setIdle])
  useIdleTimer({
    timeout: IDLE_TIMEOUT_MS,
    onIdle,
    onActive,
    onAction: onActive
  })

  /* Expand / Collapse the view */
  const { isOverviewFullScreen } = useAppContext()
  const setIsOverviewFullScreen = useSetIsOverviewFullScreen()
  const onExpandToggle = useCallback(() => {
    setIsOverviewFullScreen(!isOverviewFullScreen)
  }, [isOverviewFullScreen, setIsOverviewFullScreen])

  return useMemo(() => {
    return {
      settings: {
        ...settings
      },
      items: dashboardItems,
      root,
      highlightedView,
      changeHighlightedView,
      idle,
      expanded: isOverviewFullScreen,
      onExpandToggle
    }
  }, [settings, dashboardItems, root, highlightedView, changeHighlightedView, idle, isOverviewFullScreen, onExpandToggle])
}

export const useDashboardSettings = () => {
  return useContext(GenericWheelContext)
}

const GenericWheelProvider = ({ settings, dashboardItems, root, children }) => {
  const settingsValues = useSettingsValues(settings, dashboardItems, root)

  return (
    <GenericWheelContext.Provider value={settingsValues}>
      {children}
    </GenericWheelContext.Provider>
  )
}

GenericWheelProvider.propTypes = {
  settings: PropTypes.object.isRequired,
  dashboardItems: PropTypes.object.isRequired,
  root: PropTypes.string,
  children: PropTypes.node
}

export default GenericWheelProvider
